import posthog from "posthog-js";
import { getCustomerIdentifier } from "./api";
import { getOrCreateDeviceId } from "./identity";

const POSTHOG_KEY = "phc_oCiIlzuuDuNorR3Gq87WwFqeRUAp3ARIR8TDuZ3LlCP";
const POSTHOG_HOST = "https://us.i.posthog.com";

const FLAG_KEY = "subscription-cta-ab-test";

let lastPublishedSessionId: string | null = null;

type PixelCustomer = {
  shopify_customer_id?: string | number | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

const knownCustomer: {
  shopify_customer_id: string | null;
  customer_email: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
} = {
  shopify_customer_id: null,
  customer_email: null,
  customer_first_name: null,
  customer_last_name: null,
};

function nonEmpty(v: string | number | null | undefined): string | null {
  if (v == null) return null;
  const s = String(v);
  return s !== "" ? s : null;
}

function publishKnownCustomer(): void {
  const hasAny = Object.values(knownCustomer).some((v) => v != null);
  if (!hasAny) return;
  try {
    const Shopify = (
      window as unknown as {
        Shopify?: {
          analytics?: { publish?: (name: string, data: unknown) => void };
        };
      }
    ).Shopify;
    if (Shopify?.analytics?.publish) {
      Shopify.analytics.publish("socialocean.customer_info", knownCustomer);
    }
  } catch {
    /* no-op: pixel bridge is best-effort */
  }
  // Mirror customer attributes onto the PostHog person without changing
  // `distinct_id` (no `identify()`), so the device-id-keyed person gets
  // enriched with email / name / Shopify customer id while the bucket
  // assignment for `subscription-cta-ab-test` stays stable.
  try {
    posthog.setPersonProperties(knownCustomer);
  } catch {
    /* no-op: posthog may not be loaded yet; will be set on the next change */
  }
}

/**
 * Merge the given customer fields into the in-memory known-customer state and
 * publish to the Shopify pixel. Safe to call repeatedly with partial info — the
 * latest non-null value wins per field, and every call re-publishes the full
 * merged state so the pixel always has the most complete picture.
 */
export function setKnownCustomer(customer: PixelCustomer): void {
  const next = {
    shopify_customer_id: nonEmpty(customer.shopify_customer_id),
    customer_email: nonEmpty(customer.email),
    customer_first_name: nonEmpty(customer.first_name),
    customer_last_name: nonEmpty(customer.last_name),
  };
  let changed = false;
  for (const key of Object.keys(next) as Array<keyof typeof next>) {
    if (next[key] != null && next[key] !== knownCustomer[key]) {
      knownCustomer[key] = next[key];
      changed = true;
    }
  }
  if (changed) publishKnownCustomer();
}

/**
 * Forget the customer identity held for the Shopify pixel and the PostHog
 * person, on logout.
 *
 * `setKnownCustomer` only ever merges non-null values, so without this the
 * previous customer's email and name keep being published after an account
 * switch. Publishes the now-empty state so the pixel sees the change too;
 * `distinct_id` stays on the device id, as it does everywhere else, which
 * keeps A/B bucketing stable across the logout.
 */
export function clearKnownCustomer(): void {
  let changed = false;
  for (const key of Object.keys(knownCustomer) as Array<
    keyof typeof knownCustomer
  >) {
    if (knownCustomer[key] != null) {
      knownCustomer[key] = null;
      changed = true;
    }
  }
  if (!changed) return;
  try {
    const Shopify = (
      window as unknown as {
        Shopify?: {
          analytics?: { publish?: (name: string, data: unknown) => void };
        };
      }
    ).Shopify;
    Shopify?.analytics?.publish?.("socialocean.customer_info", knownCustomer);
  } catch {
    /* no-op: pixel bridge is best-effort */
  }
}

function publishSyncCustomerOnInit(): void {
  try {
    const ident = getCustomerIdentifier();
    if (ident?.type === "id") {
      setKnownCustomer({ shopify_customer_id: ident.shopify_customer_id });
    }
  } catch {
    /* no-op */
  }
}

function publishPostHogSessionToPixel(): void {
  try {
    const Shopify = (
      window as unknown as {
        Shopify?: {
          analytics?: { publish?: (name: string, data: unknown) => void };
        };
      }
    ).Shopify;
    if (!Shopify?.analytics?.publish) return;

    const sessionId = posthog.get_session_id?.() ?? null;
    const distinctId = posthog.get_distinct_id?.() ?? null;
    const deviceId =
      (posthog.get_property?.("$device_id") as string | undefined) ?? null;

    Shopify.analytics.publish("socialocean.posthog_session", {
      session_id: sessionId,
      distinct_id: distinctId,
      device_id: deviceId,
    });
    lastPublishedSessionId = sessionId;

    // Expose the same identity triple to other same-page widgets (e.g. the
    // review widget) via a shared contract: a sessionStorage key + a
    // same-document event. sessionStorage (NOT localStorage) so a rotated/stale
    // session id from a previous session is never reused — it's rewritten fresh
    // on each load and on every session change.
    try {
      const identity = {
        session_id: sessionId,
        distinct_id: distinctId,
        device_id: deviceId,
      };
      sessionStorage.setItem(
        "socialocean_posthog_identity",
        JSON.stringify(identity),
      );
      window.dispatchEvent(
        new CustomEvent("social-ocean:posthog-identity", { detail: identity }),
      );
    } catch {
      /* no-op: storage may be unavailable (e.g. private mode) */
    }
  } catch {
    /* no-op: pixel bridge is best-effort */
  }
}

export function initPostHog(): void {
  if (typeof window === "undefined") return;

  const deviceId = getOrCreateDeviceId();

  // Bootstrap PostHog with our device id so it's treated as the identified
  // `distinct_id` from t=0. This avoids the anonymous→identified flag-reload
  // that was previously triggered by `ph.identify(...)` inside `loaded:` and
  // caused duplicate `cta.impression` events with potentially different
  // `ab_variant` values per person (the 3–6% mixed-bucket cohort).
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    persistence: "localStorage",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    bootstrap: {
      distinctID: deviceId,
      isIdentifiedID: true,
    },
    loaded: () => {
      publishPostHogSessionToPixel();
      publishSyncCustomerOnInit();
    },
  });

  const onSessionId = (
    posthog as unknown as { onSessionId?: (cb: () => void) => void }
  ).onSessionId;
  if (typeof onSessionId === "function") {
    onSessionId.call(posthog, () => publishPostHogSessionToPixel());
  } else {
    setInterval(() => {
      const current = posthog.get_session_id?.() ?? null;
      if (current && current !== lastPublishedSessionId) {
        publishPostHogSessionToPixel();
      }
    }, 60_000);
  }

  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(FLAG_KEY) || "control";
    posthog.setPersonProperties({ banner_variant: variant });
    window.dispatchEvent(
      new CustomEvent("social-ocean:ab-variant", {
        detail: { variant },
      }),
    );
  });
}
